import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import cx from 'classnames'
import useResizeObserver from 'use-resize-observer'
import { motion } from 'framer-motion'
import { useIntersectionObserver, useMediaQuery } from 'usehooks-ts'
import Link from 'next/link'

import { LoadingSpinner, PrimaryButtonLink } from 'ui'
import ActionButton from './ActionButton'
import { MlsAttribution } from './attribution/MlsAttribution'
import ListingMap from '../ListingMap'
import AcreProducts, {
  ListingPaymentBreakdown,
  ProductsForm,
} from './AcreProducts'

import { Listing } from '../../services/getListings'
import { IndividualListing } from '../../services/getListingById'
import { useCustomer } from '../../services/customer/loadAttributes'
import useAuth from '../../hooks/useAuth'
import { useTracking } from '../../services/context/TrackingContext'
import { useAppState } from '../../services/context/AppContext'
import { selectPayment } from '../../services/flags/selectPayment'

import { numberToCurrency } from 'utils'

import { CalculatorPageContent, Product } from '../../hooks/useAcreCalculator'
import Chart from '../calculator/Chart'

const NavItem: FC<{
  isActive?: boolean
  scrollToNavItem: (left: number) => void
  scrollToSection: () => void
}> = ({ children, isActive, scrollToNavItem, scrollToSection }) => {
  const containerPaddingLeft = 12
  const itemRef = useRef<HTMLLIElement>(null)

  useEffect(() => {
    if (isActive) {
      scrollToNavItem(
        itemRef?.current?.getBoundingClientRect().left ?? containerPaddingLeft
      )
    }
  }, [children, isActive, scrollToNavItem])

  return (
    <li className="relative" ref={itemRef}>
      <button
        className={cx('relative px-12 py-20', {
          'font-semibold text-teal-3': isActive,
        })}
        onClick={scrollToSection}
      >
        <span className="invisible whitespace-nowrap font-semibold">
          {children}
        </span>
        <span className="absolute left-12">{children}</span>
      </button>
      {isActive ? (
        <motion.div
          layoutId="currrentModalSection"
          className="absolute inset-x-12 bottom-0 h-2 bg-teal-3"
        />
      ) : null}
    </li>
  )
}

const Sidebar = ({
  listing,
  detailedListing,
  calculator,
  mobileContainerRef,
  galleryHeight,
}: {
  listing: Listing | null
  detailedListing: IndividualListing | null
  calculator: CalculatorPageContent
  mobileContainerRef: React.RefObject<HTMLDivElement>
  galleryHeight: number
}) => {
  const { t } = useTranslation(['homes', 'common'])
  const { isAuthenticated } = useAuth()
  const { track } = useTracking()
  const {
    state: { globalFlags },
  } = useAppState()

  const [homeDescription, setHomeDescription] = useState('')
  const { data: customerData } = useCustomer()

  const containerRef = useRef<HTMLDivElement>(null)
  const productsSectionRef = useRef(null)
  const aboutSectionRef = useRef(null)
  const paymentSectionRef = useRef(null)
  const mapSectionRef = useRef(null)
  const chartSectionRef = useRef(null)
  const stickyNavRef = useRef<HTMLDivElement>(null)

  const { ref: stickyDetailsRef, height: stickyDetailsHeight = 0 } =
    useResizeObserver<HTMLDivElement>({
      box: 'border-box',
    })

  const { ref: stickyCtaRef, height: stickyCtaHeight = 0 } =
    useResizeObserver<HTMLDivElement>({
      box: 'border-box',
    })

  const { height: productsSectionHeight = 0 } =
    useResizeObserver<HTMLDivElement>({
      box: 'border-box',
      ref: productsSectionRef,
    })

  const { height: aboutSectionHeight = 0 } = useResizeObserver<HTMLDivElement>({
    box: 'border-box',
    ref: aboutSectionRef,
  })

  const { height: paymentSectionHeight = 0 } =
    useResizeObserver<HTMLDivElement>({
      box: 'border-box',
      ref: paymentSectionRef,
    })

  const { height: mapSectionHeight = 0 } = useResizeObserver<HTMLDivElement>({
    box: 'border-box',
    ref: mapSectionRef,
  })

  const observerOptions = {
    rootMargin: '-65% 0px -35%',
  }

  const [activeSection, setActiveSection] = useState<
    'about' | 'payment' | 'map' | 'chart'
  >('about')
  // Mirrors the product cards' selection for the payment breakdown.
  const [selectedProduct, setSelectedProduct] = useState<Product>('saver')

  const aboutObserverEntry = useIntersectionObserver(
    aboutSectionRef,
    observerOptions
  )
  const paymentObserverEntry = useIntersectionObserver(
    paymentSectionRef,
    observerOptions
  )
  const mapObserverEntry = useIntersectionObserver(
    mapSectionRef,
    observerOptions
  )

  const chartObserverEntry = useIntersectionObserver(
    chartSectionRef,
    observerOptions
  )

  const isMobileLayout = useMediaQuery('(max-width: 63.9375em)') // i.e. lg-d
  const scrollToBaseOffset = isMobileLayout
    ? galleryHeight + stickyDetailsHeight + stickyCtaHeight
    : 0

  useEffect(() => {
    if (aboutObserverEntry?.isIntersecting) {
      setActiveSection('about')
    }
    if (paymentObserverEntry?.isIntersecting) {
      setActiveSection('payment')
    }
    if (mapObserverEntry?.isIntersecting) {
      setActiveSection('map')
    }
    if (chartObserverEntry?.isIntersecting) {
      setActiveSection('chart')
    }
  }, [
    aboutObserverEntry?.isIntersecting,
    chartObserverEntry?.isIntersecting,
    mapObserverEntry?.isIntersecting,
    paymentObserverEntry?.isIntersecting,
  ])

  const scrollToNavItem = useCallback((left: number) => {
    left = left - (stickyNavRef.current?.getBoundingClientRect().left ?? 0)
    stickyNavRef?.current?.scrollTo({
      left,
      behavior: 'smooth',
    })
  }, [])

  const scrollToSection = useCallback(
    (top: number) => {
      const container = isMobileLayout ? mobileContainerRef : containerRef
      container?.current?.scrollTo({
        top,
        behavior: 'smooth',
      })
    },
    [isMobileLayout, mobileContainerRef]
  )

  const scrollToPayment = useCallback(
    () =>
      scrollToSection(
        scrollToBaseOffset + productsSectionHeight + aboutSectionHeight
      ),
    [
      scrollToSection,
      scrollToBaseOffset,
      productsSectionHeight,
      aboutSectionHeight,
    ]
  )

  useEffect(() => {
    if (listing) {
      const doc = new DOMParser().parseFromString(
        listing.home_description,
        'text/html'
      )
      setHomeDescription(doc.documentElement.textContent ?? '')
    }
  }, [listing])

  if (!listing) {
    return (
      <div className="relative max-w-full">
        <div className="space-y-8 p-24">
          <div className="relative h-24 w-4/5 overflow-hidden rounded-sm bg-grey-3 after:absolute after:inset-0 after:animate-pulse after:bg-grey-4" />
          <div className="relative h-24 overflow-hidden rounded-sm bg-grey-3 after:absolute after:inset-0 after:animate-pulse after:bg-grey-4" />
          <div className="relative h-20 w-2/5 overflow-hidden rounded-sm bg-grey-3 after:absolute after:inset-0 after:animate-pulse after:bg-grey-4" />
        </div>
        <div className="border-t p-24">
          <div className="relative h-48 overflow-hidden rounded-md bg-grey-3 after:absolute after:inset-0 after:animate-pulse after:bg-grey-4" />
        </div>
        <LoadingSpinner />
      </div>
    )
  }

  const payment = selectPayment(listing, globalFlags)
  const paymentHeight = payment.kind === 'v3' ? paymentSectionHeight : 0
  const breakdown =
    payment.kind === 'v3' ? (
      <ListingPaymentBreakdown
        listing={listing}
        calculator={calculator}
        product={selectedProduct}
        payment={payment}
      />
    ) : null

  return (
    <div
      className="relative z-10 max-w-full bg-white lg:overflow-auto"
      ref={containerRef}
    >
      {/* House Details */}
      <div
        ref={stickyDetailsRef}
        className="border-b bg-white p-24 lg:sticky lg:top-0 lg:z-10"
      >
        <p
          className={cx(
            'font-semibold leading-tight tracking-2',
            'md-d:text-center',
            'md:hd-lg'
          )}
        >
          {`${listing.beds} beds • ${listing.baths} baths • ${
            listing.sqft ? listing.sqft.toLocaleString() : 0
          } Sq. Ft.`}
        </p>
        <address
          className={cx(
            'mt-8 not-italic leading-tight text-grey-7',
            'md-d:text-center',
            'md:text-18 md:leading-snug'
          )}
        >
          {listing.address}
        </address>
        <p
          className={cx(
            'mt-4 text-14 font-semibold leading-relaxed text-grey-7',
            'md-d:text-center',
            'md:mt-8 md:text-16'
          )}
        >
          {t('listedPrice')} {numberToCurrency(listing.list_price)}
        </p>
      </div>

      {/* Boost vs Saver */}
      <div
        ref={productsSectionRef}
        className="relative bg-grey-2 p-24 before:absolute before:inset-x-0 before:bottom-px before:h-px before:bg-grey-3"
      >
        <ProductsForm
          listingId={listing.id}
          handleSeeTheNumbersButtonClick={() => {
            track(
              makeCalculatorOpenedEventTrack({
                listingPrice: Math.round(listing.list_price),
              })
            )
          }}
        >
          <AcreProducts
            calculator={calculator}
            listing={listing}
            initialProduct="saver"
            trackEventName="Property Listing Model Selected"
            onProductChange={setSelectedProduct}
            onSeeBreakdown={scrollToPayment}
          />
        </ProductsForm>
      </div>

      {/* CTA */}
      {/* ::before is used to cover up potential subpixel gap between details & sticky top position */}
      <div
        ref={stickyCtaRef}
        className={cx(
          'relative bg-white lg:sticky lg:z-[9] lg-d:!top-0',
          'before:absolute before:inset-x-0 before:-top-px before:h-px before:bg-white'
        )}
        style={{ top: `${stickyDetailsHeight}px` }}
      >
        {isAuthenticated ? (
          <div className="p-24">
            <ActionButton
              customerApprovalStatus={customerData?.approval.status}
              customerOfferStatus={customerData?.homeOffer?.status}
              existingOffer={listing.existing_offer}
              listingId={listing.id}
              offMarket={!listing.live}
              propertyStatus={listing.property_status}
            />
          </div>
        ) : (
          <div className="p-16">
            <Link href="/get-started" passHref>
              <PrimaryButtonLink
                className="block"
                size="lg"
                onClick={() =>
                  track({
                    event: 'Sign Up Flow Opened',
                    properties: {
                      link_type: 'Button',
                      link_location: 'Property Listing',
                      link_text: t('gatedListingCalculator.cta'),
                    },
                  })
                }
              >
                {t('gatedListingCalculator.cta')}
              </PrimaryButtonLink>
            </Link>
          </div>
        )}
      </div>

      {/* Sticky nav */}
      <nav
        ref={stickyNavRef}
        aria-label="Listing Details Navigation"
        className="sticky z-10 overflow-x-auto border-t bg-white px-12 shadow-md before:absolute before:inset-x-0 before:-top-px before:h-px before:bg-white lg-d:!-top-px"
        style={{
          top: `${stickyDetailsHeight + stickyCtaHeight}px`,
        }}
      >
        <ul className="inline-flex overflow-hidden">
          <NavItem
            isActive={activeSection === 'about'}
            scrollToNavItem={scrollToNavItem}
            scrollToSection={() =>
              scrollToSection(scrollToBaseOffset + productsSectionHeight)
            }
          >
            {t('modalNav.aboutThisHome')}
          </NavItem>
          {breakdown && (
            <NavItem
              isActive={activeSection === 'payment'}
              scrollToNavItem={scrollToNavItem}
              scrollToSection={scrollToPayment}
            >
              {t('modalNav.payment')}
            </NavItem>
          )}
          <NavItem
            isActive={activeSection === 'map'}
            scrollToNavItem={scrollToNavItem}
            scrollToSection={() =>
              scrollToSection(
                scrollToBaseOffset +
                  productsSectionHeight +
                  aboutSectionHeight +
                  paymentHeight
              )
            }
          >
            {t('modalNav.location')}
          </NavItem>
          <NavItem
            isActive={activeSection === 'chart'}
            scrollToNavItem={scrollToNavItem}
            scrollToSection={() =>
              scrollToSection(
                scrollToBaseOffset +
                  productsSectionHeight +
                  aboutSectionHeight +
                  paymentHeight +
                  mapSectionHeight
              )
            }
          >
            {t('modalNav.chart')}
          </NavItem>
        </ul>
      </nav>

      {/* About this home */}
      <div className="space-y-8 bg-white p-24" ref={aboutSectionRef}>
        <h3 className="text-16 font-semibold leading-[22px] text-grey-8">
          {t('aboutThisHome')}
        </h3>
        <p className="whitespace-pre-line leading-relaxed text-grey-7">
          {homeDescription}
        </p>
        {detailedListing && (
          <MlsAttribution detailedListing={detailedListing} />
        )}
      </div>

      {/* Monthly payment breakdown: a plain section, like About this home */}
      {breakdown && (
        <section
          aria-labelledby="listing-payment-breakdown-heading"
          className="space-y-8 border-t bg-white p-24"
          ref={paymentSectionRef}
        >
          <h3
            id="listing-payment-breakdown-heading"
            className="text-16 font-semibold leading-[22px] text-grey-8"
          >
            {t('paymentBreakdown.heading')}
          </h3>
          {breakdown}
        </section>
      )}

      {/* Map */}
      <div ref={mapSectionRef}>
        {listing.geolocation ? (
          <ListingMap
            lat={listing.geolocation.latitude}
            lng={listing.geolocation.longitude}
          />
        ) : null}
      </div>

      {/* HPA Charts */}
      <div className="bg-grey-2 p-24" ref={chartSectionRef}>
        <Chart calculator={calculator} listing={listing} />
      </div>
    </div>
  )
}

type CalculatorOpenedEventTrackVariables = {
  readonly listingPrice: number
}

function makeCalculatorOpenedEventTrack(
  variables: CalculatorOpenedEventTrackVariables
) {
  return {
    event: 'Calculator Opened',
    properties: {
      target_home_price: variables.listingPrice,
      link_type: 'Text',
      link_cta: 'See the numbers',
      link_location: 'Listing Detail - Top Right',
      link_priority: 2,
      link_color: 'Teal',
    },
  }
}

export default Sidebar
